// istanbul ignore file
/**
 * TODO: deprecate this file and use the new useApplicationNotifications hook
 */
import React, { Component, createRef, RefObject } from 'react'
import { render } from 'react-dom'
import { FixedMessage, Text } from '@adc/ui-core'

export interface Notification {
  title?: string
  message?: string
}

export interface Notifier {
  success: (notification: Notification) => void
  info: (notification: Notification) => void
  warn: (notification: Notification) => void
  error: (notification: Notification) => void
}

interface NotificationState {
  isVisible: boolean
  message?: string | null
  title?: string | null
  role?: string
}

/**
 * TODO: Need to support roles based on notifier interface
 */
class FixedMessageNotification extends Component<object, NotificationState> {
  cleanupTimeout: number | null

  constructor(props: object) {
    super(props)

    this.state = { isVisible: false }

    // TODO: Clean me up when I unmount Connor. Don't leave me hanging here you British oaf
    this.cleanupTimeout = null
  }

  componentWillUnmount() {
    if (this.cleanupTimeout) {
      window.clearTimeout(this.cleanupTimeout)
    }
  }

  notify(notification: Notification & { role?: string }) {
    this.setState({
      isVisible: true,
      ...notification,
    })

    // TODO: I need to investigate differnt patterns here. We need to consider what happens when multiple updates come
    // at the same time. Perhaps we destroy the existing timeout and start agin no matter and that's just fine.
    //
    // NOTE: This seems odd, but it's exactly what apps do right now, just abstracted away in async actions.
    if (this.cleanupTimeout) {
      window.clearTimeout(this.cleanupTimeout)
    }
    this.cleanupTimeout = window.setTimeout(() => this.resetState(), 5000)
  }

  resetState() {
    this.setState({
      isVisible: false,
      message: null,
      title: null,
    })
  }

  render() {
    const { isVisible, message, title, role } = this.state

    // NOTE: This has a `transition` in some apps. I removed that here because if we remove the message and title, but
    // transition the containing element, there exists a janky empty box transitioning for half a second. We could solve
    // that with 2 timeouts, one for the `isVisible` to transition out, and then a second to reset the full state to
    // empty out the text and message.
    return (
      <div data-elm-id='Message_Notification auctionapp_notifier_fixed'>
        <FixedMessage
          centered
          role={role}
          isCloseable={false}
          style={{
            position: 'fixed',
            right: '20%',
            left: '20%',
            width: 'auto',
            zIndex: 80,
            top: isVisible ? '70px' : '-100%',
            opacity: isVisible ? 1 : 0,
            textAlign: 'center',
          }}
        >
          <div data-elm-id='Message'>
            {title && (
              <Text.Header
                data-elm-id='Message-title auctionapp_notifier_fixed_title'
                level={3}
                className='u-mb-1'
              >
                {title}
              </Text.Header>
            )}
            {message && (
              <Text
                data-elm-id='Message-message auctionapp_notifier_fixed_message'
                children={message}
              />
            )}
          </div>
        </FixedMessage>
      </div>
    )
  }
}

export class NullNotifier implements Notifier {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  success(_notification: Notification) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  info(_notification: Notification) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  warn(_notification: Notification) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  error(_notification: Notification) {}
}

export class DebuggingNotifier implements Notifier {
  success(notification: Notification) {
    /* eslint-disable-next-line no-console */
    console.log(notification)
  }

  info(notification: Notification) {
    /* eslint-disable-next-line no-console */
    console.log(notification)
  }

  warn(notification: Notification) {
    console.warn(notification)
  }

  error(notification: Notification) {
    console.error(notification)
  }
}

export class FixedMessageNotifier implements Notifier {
  private _containerDomNode: HTMLDivElement
  private _notificationSystem: RefObject<FixedMessageNotification>

  constructor() {
    this._containerDomNode = document.createElement('div')
    this._notificationSystem = createRef<FixedMessageNotification>()

    document.body.appendChild(this._containerDomNode)

    render(
      <FixedMessageNotification ref={this._notificationSystem} />,
      this._containerDomNode
    )
  }

  private _notifyAtLevel({
    title,
    message,
    role,
  }: Notification & { role: string }) {
    this._notificationSystem.current?.notify({
      title,
      message,
      role,
    })
  }

  success(notification: Notification) {
    this._notifyAtLevel({
      ...notification,
      role: 'success',
    })
  }

  info(notification: Notification) {
    this._notifyAtLevel({
      ...notification,
      role: 'information',
    })
  }

  warn(notification: Notification) {
    this._notifyAtLevel({
      ...notification,
      role: 'warning',
    })
  }

  error(notification: Notification) {
    this._notifyAtLevel({
      ...notification,
      role: 'error',
    })
  }
}
